import { creatorbase } from '@round/api';
import BrandForm, { BrandFormValues } from '../BrandForm/BrandForm';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { Modal, Button } from '@round/whitelabel-ui';
import { showNotification } from 'helpers';
import { brandFormValidationSchema, convertBrandApiErrorsToFormikErrors } from '../BrandForm/BrandForm.helpers';
import styles from './CreateBrandModal.module.css';
import { createBrandUtils, useCreateBrand } from '@round/creatorbase-helpers/api-hooks';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreated?: (brand: creatorbase.Brand) => void;
};

const initialValues: BrandFormValues = {
    name: '',
    image: null,
    team: null,
};

const CreateBrandModal = ({ isOpen, onClose, onCreated }: Props) => {
    const { create } = useCreateBrand();

    const handleSubmit = async (values: BrandFormValues, helpers: FormikHelpers<BrandFormValues>) => {
        const data: creatorbase.PostBrandData = {
            name: values.name,
            team_id: values.team!.value,
            image: values.image,
        };

        try {
            const brand = await create(data);
            showNotification('Artist created', 'info');
            helpers.resetForm();
            onCreated?.(brand);
            onClose();
        } catch (e) {
            if (!(e instanceof Error) || !createBrandUtils.isExpectedErrorResponse(e)) {
                showNotification('Could not create artist', 'error');
                return;
            }

            if (Array.isArray(e.response.data)) {
                showNotification(e.response.data.join('. '), 'error');
                return;
            }

            if ('detail' in e.response.data) {
                showNotification('Could not create artist', 'error');
                return;
            }

            helpers.setErrors(convertBrandApiErrorsToFormikErrors(e.response.data));
        }
    };

    const formik = useFormik<BrandFormValues>({
        validationSchema: brandFormValidationSchema,
        initialValues,
        onSubmit: handleSubmit,
    });

    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        formik.resetForm();
        onClose();
    };

    return (
        <Modal
            className={styles.modal}
            isOpen={isOpen}
            onClose={handleClose}
            closeOnOverlayClick={!formik.isSubmitting}
            ariaLabel="create artist modal"
        >
            <Modal.Header>
                <Modal.Title>Create an artist</Modal.Title>
            </Modal.Header>
            <FormikProvider value={formik}>
                <Modal.Body>
                    <BrandForm />
                </Modal.Body>

                <Modal.Actions>
                    <Button
                        appearance="primary"
                        onClick={() => formik.handleSubmit()}
                        disabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    >
                        Create artist
                    </Button>

                    <Button appearance="ghost" onClick={handleClose} disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                </Modal.Actions>
            </FormikProvider>
        </Modal>
    );
};

export default CreateBrandModal;
